import React, { useState, useEffect } from 'react';
import { Campaign, CampaignCreate } from '../types/campaign';
import {
  TextField,
  Button,
  Box,
  Typography,
  Grid,
  InputAdornment,
} from '@mui/material';

interface CampaignFormProps {
  onSubmit: (data: CampaignCreate) => Promise<void>;
  onCancel: () => void;
  initialData?: Campaign | null;
}

const CampaignForm: React.FC<CampaignFormProps> = ({ onSubmit, onCancel, initialData }) => {
  const [formData, setFormData] = useState<CampaignCreate>({
    title: '',
    description: '',
    website_url: '',
    image_url: '',
    category: '',
    keywords: '',
    budget: 0,
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title,
        description: initialData.description,
        website_url: initialData.website_url,
        image_url: initialData.image_url || '',
        category: initialData.category,
        keywords: initialData.keywords || '',
        budget: initialData.budget,
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'budget' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      color: '#E5E7EB',
      '& fieldset': {
        borderColor: '#4B5563',
      },
      '&:hover fieldset': {
        borderColor: '#60A5FA',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#3B82F6',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#9CA3AF',
      '&.Mui-focused': {
        color: '#60A5FA',
      },
    },
    '& .MuiInputAdornment-root': {
      color: '#9CA3AF',
    },
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" sx={{ color: '#E5E7EB', mb: 3 }}>
        {initialData ? 'Edit Campaign' : 'Create New Campaign'}
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Campaign Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            multiline
            rows={4}
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Website URL"
            name="website_url"
            type="url"
            value={formData.website_url}
            onChange={handleChange}
            required
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Image URL"
            name="image_url"
            type="url"
            value={formData.image_url}
            onChange={handleChange}
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Keywords"
            name="keywords"
            value={formData.keywords}
            onChange={handleChange}
            placeholder="Separate with commas"
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Budget"
            name="budget"
            type="number"
            value={formData.budget}
            onChange={handleChange}
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={textFieldSx}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Impressions"
            value={initialData ? initialData.impressions ?? 0 : 0}
            InputProps={{ readOnly: true }}
            disabled
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Clicks"
            value={initialData ? initialData.clicks ?? 0 : 0}
            InputProps={{ readOnly: true }}
            disabled
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="CTR (%)"
            value={initialData && initialData.impressions ? ((initialData.clicks ?? 0) / initialData.impressions * 100).toFixed(2) : '0.00'}
            InputProps={{ readOnly: true }}
            disabled
            sx={textFieldSx}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          onClick={onCancel}
          sx={{
            color: '#9CA3AF',
            '&:hover': {
              bgcolor: '#374151',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          sx={{
            bgcolor: '#3B82F6',
            color: '#fff',
            '&:hover': {
              bgcolor: '#2563EB',
            },
          }}
        >
          {initialData ? 'Update Campaign' : 'Create Campaign'}
        </Button>
      </Box>
    </Box>
  );
};

export default CampaignForm; 